import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dog, Loader2, Mail, Phone, Printer, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../../lib/api";
import WaiverDog, { rabiesStatus } from "./WaiverDog";

const WAIVER_URL = `${window.location.origin}/waiver`;

function matches(w, q) {
  const hay = [w.name, w.email, w.phone, ...(w.dogs || []).map((d) => d.name)].join(" ").toLowerCase();
  return q.split(/\s+/).filter(Boolean).every((t) => hay.includes(t));
}

function WaiverQrCard() {
  return (
    <div data-testid="waiver-qr-card" className="rounded-3xl border border-lavender-700/10 bg-white p-6 shadow-soft print:border-0 print:shadow-none">
      <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
        <div className="rounded-2xl bg-white p-3 ring-1 ring-lavender-700/10">
          <QRCodeSVG value={WAIVER_URL} size={160} bgColor="#ffffff" fgColor="#4c1d95" level="M" data-testid="waiver-qr" />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <p className="font-serif text-2xl text-stone-900">Scan to sign the grooming waiver</p>
          <p className="mt-2 text-sm text-stone-600">Print this and keep it at the front desk. Customers scan it with their phone, read the waiver and sign in under a minute. Signed waivers appear below.</p>
          <p className="mt-3 break-all text-xs text-stone-400" data-testid="waiver-url">{WAIVER_URL}</p>
          <button type="button" onClick={() => window.print()} data-testid="waiver-print-button" className="mt-4 inline-flex items-center gap-2 rounded-full bg-lavender-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-lavender-800 print:hidden">
            <Printer size={15} /> Print QR sign
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WaiversTab() {
  const [waivers, setWaivers] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/admin/waivers").then((r) => setWaivers(r.data)).catch(() => toast.error("Could not load waivers"));
  }, []);

  const remove = async (w) => {
    if (!window.confirm(`Delete the waiver for ${w.name} and all their dogs?`)) return;
    try {
      await api.delete(`/admin/waivers/${w.id}`);
      setWaivers((ws) => ws.filter((x) => x.id !== w.id));
      toast.success("Waiver deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const patchDog = (wid, did, patch) =>
    setWaivers((ws) => ws.map((w) => (w.id !== wid ? w : { ...w, dogs: w.dogs.map((d) => (d.id === did ? { ...d, ...patch } : d)) })));
  const removeDog = (wid, did) =>
    setWaivers((ws) => ws.map((w) => (w.id !== wid ? w : { ...w, dogs: w.dogs.filter((d) => d.id !== did) })).filter((w) => w.dogs.length));

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => (waivers || []).filter((w) => !q || matches(w, q)), [waivers, q]);
  const allDogs = (waivers || []).flatMap((w) => w.dogs || []);
  const flagged = allDogs.filter((d) => ["expired", "soon"].includes(rabiesStatus(d.rabiesExpiry).key)).length;
  const missing = allDogs.filter((d) => !d.rabiesFile).length;

  return (
    <div className="space-y-6">
      <WaiverQrCard />
      <div className="print:hidden">
        <p className="text-sm text-stone-500">Customers sign the waiver and upload their rabies record from the Waiver tab on the site. One waiver per owner; each dog is listed with its own rabies record. Enter each dog's rabies expiry date to get flagged when it runs out.</p>
        {waivers && waivers.length > 0 && (
          <>
            <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold" data-testid="waivers-summary">
              <span className="rounded-full bg-lavender-100 px-3 py-1 text-lavender-800">{waivers.length} owner{waivers.length === 1 ? "" : "s"} · {allDogs.length} dog{allDogs.length === 1 ? "" : "s"}</span>
              <span data-testid="waivers-flagged-count" className={`rounded-full px-3 py-1 ${flagged ? "bg-red-100 text-red-700" : "bg-grass-100 text-grass-700"}`}>{flagged} rabies expired or expiring soon</span>
              <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">{missing} without uploaded record</span>
            </div>
            <div className="relative mt-4 max-w-md">
              <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="waivers-search-input"
                placeholder="Search by owner, dog, email or phone…"
                className="w-full rounded-full border border-lavender-700/15 bg-white py-2.5 pl-10 pr-10 text-sm text-stone-800 placeholder:text-stone-400 focus:border-lavender-500 focus:outline-none focus:ring-2 focus:ring-lavender-500/30"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} data-testid="waivers-search-clear" aria-label="Clear search" className="absolute right-3 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-stone-400 hover:bg-lavender-100 hover:text-stone-700">
                  <X size={14} />
                </button>
              )}
            </div>
            {q && <p className="mt-2 text-xs text-stone-500" data-testid="waivers-search-count">{visible.length} match{visible.length === 1 ? "" : "es"}</p>}
          </>
        )}
        {!waivers ? (
          <div className="grid place-items-center py-16" data-testid="waivers-loading"><Loader2 className="animate-spin text-lavender-600" size={26} /></div>
        ) : !waivers.length ? (
          <div data-testid="waivers-empty" className="mt-5 rounded-3xl border-2 border-dashed border-lavender-300 bg-white p-12 text-center">
            <Dog size={30} className="mx-auto text-lavender-400" />
            <p className="mt-4 font-serif text-xl text-stone-800">No signed waivers yet</p>
            <p className="mt-1 text-sm text-stone-500">Waivers signed from the QR code or the Waiver tab will land here.</p>
          </div>
        ) : !visible.length ? (
          <div data-testid="waivers-no-results" className="mt-5 rounded-3xl border border-lavender-700/10 bg-white p-10 text-center text-sm text-stone-500">
            No waivers match "{query}".
          </div>
        ) : (
          <div className="mt-5 grid gap-4 lg:grid-cols-2" data-testid="waivers-list">
            {visible.map((w, i) => (
              <div key={w.id} data-testid={`waiver-card-${i}`} className="relative rounded-3xl border border-lavender-700/10 bg-white p-6 shadow-soft">
                <button onClick={() => remove(w)} data-testid={`waiver-delete-${i}`} aria-label="Delete waiver" className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100">
                  <Trash2 size={14} />
                </button>
                <h3 className="font-serif text-xl text-stone-900" data-testid={`waiver-owner-${i}`}>{w.name}</h3>
                <p className="mt-0.5 text-xs text-stone-400">
                  First signed {w.signed_at ? new Date(w.signed_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : ""} · {(w.dogs || []).length} dog{(w.dogs || []).length === 1 ? "" : "s"}
                </p>
                <div className="mt-3 grid gap-1.5 text-sm text-stone-700">
                  {w.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-lavender-600" /><a className="hover:text-lavender-700" href={`tel:${w.phone}`}>{w.phone}</a></p>}
                  {w.email && <p className="flex items-center gap-2 break-all" data-testid={`waiver-email-${i}`}><Mail size={14} className="shrink-0 text-lavender-600" /><a className="hover:text-lavender-700" href={`mailto:${w.email}`}>{w.email}</a></p>}
                </div>
                <div className="mt-4 space-y-3" data-testid={`waiver-dogs-${i}`}>
                  {(w.dogs || []).map((d, j) => (
                    <WaiverDog
                      key={d.id}
                      waiverId={w.id}
                      dog={d}
                      testid={`waiver-${i}-dog-${j}`}
                      canRemove={(w.dogs || []).length > 1}
                      onSaved={(expiry) => patchDog(w.id, d.id, { rabiesExpiry: expiry })}
                      onRemoved={() => removeDog(w.id, d.id)}
                      onPdf={(path) => patchDog(w.id, d.id, { waiverPdf: path })}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
